/**
 * Service Layer Permission Validation
 *
 * Wrappers that validate capabilities before allowing service operations.
 * These ensure that service functions cannot be bypassed without proper authorization.
 *
 * Pattern:
 *   await validateCapability(userRole, 'canDeleteEntries')
 *   // If validation fails, throws PermissionError
 *
 * Usage:
 *   export async function deleteEntry(id: string, userRole: Role): Promise<void> {
 *     await validateCapability(userRole, 'canDeleteEntries')
 *     // ... proceed with deletion
 *   }
 */

import type { Role, Capability } from './types'
import { canUserDo } from '@/lib/permissions'

/**
 * Custom error for permission violations
 */
export class PermissionError extends Error {
  constructor(message: string) {
    super(message)
    this.name = 'PermissionError'
  }
}

/**
 * Validate that a role has a specific capability
 * Throws PermissionError if validation fails
 *
 * @param userRole - The user's role
 * @param capability - The required capability
 * @throws PermissionError if user doesn't have capability
 */
export async function validateCapability(
  userRole: Role | null | undefined,
  capability: Capability
): Promise<void> {
  if (!userRole) {
    throw new PermissionError(`Permission denied: User must be authenticated to ${capability}`)
  }

  if (!canUserDo(userRole, capability)) {
    throw new PermissionError(
      `Permission denied: Your role does not have the ${capability} capability`
    )
  }
}

/**
 * Validate multiple capabilities (user must have at least ONE)
 * Throws PermissionError if validation fails
 *
 * @param userRole - The user's role
 * @param capabilities - Array of acceptable capabilities
 * @throws PermissionError if user doesn't have any capability
 */
export async function validateCapabilityAny(
  userRole: Role | null | undefined,
  capabilities: Capability[]
): Promise<void> {
  if (!userRole) {
    throw new PermissionError('Permission denied: User must be authenticated')
  }

  const hasAny = capabilities.some(cap => canUserDo(userRole, cap))
  if (!hasAny) {
    throw new PermissionError(
      `Permission denied: Your role does not have any of the required capabilities`
    )
  }
}

/**
 * Validate multiple capabilities (user must have ALL)
 * Throws PermissionError if validation fails
 *
 * @param userRole - The user's role
 * @param capabilities - Array of required capabilities
 * @throws PermissionError if user doesn't have all capabilities
 */
export async function validateCapabilityAll(
  userRole: Role | null | undefined,
  capabilities: Capability[]
): Promise<void> {
  if (!userRole) {
    throw new PermissionError('Permission denied: User must be authenticated')
  }

  const missing = capabilities.filter(cap => !canUserDo(userRole, cap))
  if (missing.length > 0) {
    throw new PermissionError(
      `Permission denied: Your role is missing required capabilities: ${missing.join(', ')}`
    )
  }
}

/**
 * Check if user is unit owner (for ultra-sensitive operations)
 * Throws PermissionError if not owner
 *
 * @param isOwner - Whether user is owner
 * @throws PermissionError if user is not owner
 */
export async function validateOwner(isOwner: boolean): Promise<void> {
  if (!isOwner) {
    throw new PermissionError('Permission denied: Only the unit owner can perform this action')
  }
}

/**
 * Check if role is admin/manager (for management operations)
 * Useful for intermediate validation before more specific checks
 *
 * @param userRole - The user's role
 * @throws PermissionError if not admin/manager
 */
export async function validateIsManager(userRole: Role | null | undefined): Promise<void> {
  if (!userRole || (userRole !== 'admin' && userRole !== 'manager')) {
    throw new PermissionError(
      'Permission denied: Only administrators and managers can perform this action'
    )
  }
}
